import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { payrollKeys } from './queryKeys';
import { companyDayRangeBounds } from '@/lib/timesheetTime';
import { useCompanyTimezone } from '@/hooks/useCompanyTimezone';
import type { EmployeePayrollData, PayrollEligibilityReason } from '@/services/payroll/types';

const PAGE_SIZE = 1000;

type StagedProfile = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  role: string | null;
  avatar_url?: string | null;
};

async function fetchPayrollEmployees(
  startDate: string,
  endDate: string,
  timezone: string,
  includeNonWorking: boolean
): Promise<EmployeePayrollData[]> {
  // Get current user's company
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: profile } = await supabase
    .from('profiles')
    .select('company_id')
    .eq('user_id', user.id)
    .single();

  if (!profile) throw new Error('Profile not found');

  // Fetch all active employees with their time entry stats
  const { data: employees, error } = await supabase
    .from('profiles')
    .select(`
      id,
      first_name,
      last_name,
      email,
      role,
      avatar_url
    `)
    .eq('company_id', profile.company_id)
    .eq('status', 'active')
    .order('first_name');

  if (error) throw error;
  if (!employees || employees.length === 0) return [];

  const employeeIds = employees.map(e => e.id);

  // Company-timezone UTC boundaries, half-open [start, endExclusive), so the
  // whole final local day is included — this mirrors the boundaries used by the
  // generate-payroll Edge Function.
  const { startISO, endExclusiveISO } = companyDayRangeBounds(startDate, endDate, timezone);

  // Keyset-paged fetch: never silently truncate, never silently swallow errors.
  const allTimeEntries: Array<{ id: string; profile_id: string; clock_in: string; clock_out: string | null; status: string }> = [];
  let cursor: string | null = null;
  // Hard safety bound: 100 pages = 100k rows.
  for (let page = 0; page < 100; page++) {
    let query = supabase
      .from('time_entries')
      .select('id, profile_id, clock_in, clock_out, status')
      .in('profile_id', employeeIds)
      .gte('clock_in', startISO)
      .lt('clock_in', endExclusiveISO)
      .order('id', { ascending: true })
      .limit(PAGE_SIZE);

    if (cursor) query = query.gt('id', cursor);

    const { data: pageRows, error: pageError } = await query;
    if (pageError) {
      throw new Error(`Failed to load approved time entries for this period: ${pageError.message}`);
    }
    if (!pageRows || pageRows.length === 0) break;
    allTimeEntries.push(...(pageRows as typeof allTimeEntries));
    if (pageRows.length < PAGE_SIZE) break;
    cursor = pageRows[pageRows.length - 1].id;
  }

  // Group time entries by profile_id
  const entriesByProfile = new Map<string, typeof allTimeEntries>();
  for (const entry of allTimeEntries) {
    const existing = entriesByProfile.get(entry.profile_id) || [];
    existing.push(entry);
    entriesByProfile.set(entry.profile_id, existing);
  }

  // Reasons an employee is selectable.
  const reasons = new Map<string, Set<PayrollEligibilityReason>>();
  const hoursByProfile = new Map<string, number>();

  for (const employee of employees) {
    const entries = entriesByProfile.get(employee.id) || [];
    const approvedEntries = entries.filter(te => te.status === 'approved' && te.clock_out);
    if (approvedEntries.length === 0) continue;

    const total_hours = approvedEntries.reduce((sum, te) => {
      const hours = (new Date(te.clock_out!).getTime() - new Date(te.clock_in).getTime()) / (1000 * 60 * 60);
      return sum + hours;
    }, 0);

    hoursByProfile.set(employee.id, total_hours);
    reasons.set(employee.id, new Set<PayrollEligibilityReason>(['time']));
  }

  if (includeNonWorking) {
    const withoutTime = employeeIds.filter(id => !reasons.has(id));

    if (withoutTime.length > 0) {
      // Salary / hourly employees with a compensation rule effective at period end.
      const { data: assignments, error: assignmentError } = await supabase
        .from('compensation_rule_assignments')
        .select('profile_id, compensation_rules!inner(effective_from, effective_to, is_active)')
        .in('profile_id', withoutTime)
        .eq('is_active', true)
        .eq('compensation_rules.is_active', true);

      if (assignmentError) {
        throw new Error(`Failed to load compensation assignments: ${assignmentError.message}`);
      }

      const periodEnd = new Date(`${endDate}T00:00:00Z`);
      for (const assignment of assignments || []) {
        const rule = (assignment as unknown as { compensation_rules?: { effective_from: string; effective_to: string | null } }).compensation_rules;
        if (!rule) continue;
        if (new Date(`${rule.effective_from}T00:00:00Z`) > periodEnd) continue;
        if (rule.effective_to && new Date(`${rule.effective_to}T00:00:00Z`) < periodEnd) continue;
        const set = reasons.get(assignment.profile_id) || new Set<PayrollEligibilityReason>();
        set.add('salary');
        reasons.set(assignment.profile_id, set);
      }

      // Paid-leave-only employees. Reuse the same service-role source the
      // preview and final run use, so eligibility cannot disagree with payroll.
      const { data: leaveResponse, error: leaveError } = await supabase.functions.invoke(
        'payroll-leave-data',
        { body: { profileIds: withoutTime, startDate, endDate } }
      );
      if (leaveError) {
        throw new Error(`Failed to load leave data: ${leaveError.message}`);
      }
      const leaveRecord = (leaveResponse?.data || {}) as Record<string, { approved_leave?: Array<{ computed_hours?: number }> }>;
      for (const [profileId, empLeave] of Object.entries(leaveRecord)) {
        const paidHours = (empLeave.approved_leave || []).reduce((sum, l) => sum + (l.computed_hours || 0), 0);
        if (paidHours <= 0) continue;
        const set = reasons.get(profileId) || new Set<PayrollEligibilityReason>();
        set.add('leave');
        reasons.set(profileId, set);
      }
    }
  }

  const employeeData: EmployeePayrollData[] = [];
  for (const employee of employees as StagedProfile[]) {
    const employeeReasons = reasons.get(employee.id);
    if (!employeeReasons || employeeReasons.size === 0) continue;

    const total_hours = hoursByProfile.get(employee.id) || 0;

    employeeData.push({
      id: employee.id,
      profile_id: employee.id,
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      email: employee.email,
      role: employee.role || 'STAFF',
      avatar_url: employee.avatar_url || undefined,
      total_hours,
      regular_hours: Math.min(total_hours, 40),
      overtime_hours: Math.max(total_hours - 40, 0),
      holiday_hours: 0,
      has_approved_entries: employeeReasons.has('time'),
      eligibility_reasons: Array.from(employeeReasons),
    });
  }

  return employeeData;
}

export const usePayrollEmployees = (
  startDate: string,
  endDate: string,
  includeNonWorking = false
) => {
  const timezone = useCompanyTimezone();
  return useQuery({
    queryKey: payrollKeys.employees(startDate, endDate, timezone, includeNonWorking),
    queryFn: () => fetchPayrollEmployees(startDate, endDate, timezone, includeNonWorking),
    enabled: !!startDate && !!endDate,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: false,
  });
};
